/* eslint-disable jsx-a11y/tabindex-no-positive */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  TextField,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
} from '@mui/material';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import JoditEditor from 'jodit-react';
import axios, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';

const PostSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  body: Yup.string().required('Body cannot be empty'),
});

AppPostPostDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  postId: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleComplete: PropTypes.func.isRequired,
};

export default function AppPostPostDialog({ open, postId, handleClose, handleComplete }) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const editor = useRef(null);

  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [postUrl, setPostUrl] = useState('');
  const [editorContent, setEditorContent] = useState('');

  const methods = useForm({
    resolver: yupResolver(PostSchema),
    defaultValues: { title: '', body: '' },
    mode: 'onChange',
  });

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  // Editor config
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      height: 270,
      toolbarSticky: false,
      buttons: [
        'source',
        '|',
        'bold',
        'underline',
        'italic',
        'eraser',
        '|',
        'ul',
        'ol',
        '|',
        'outdent',
        'indent',
        '|',
        'font',
        'fontsize',
        'brush',
        'paragraph',
        '|',
        'image',
        'video',
        'link',
        '|',
        'emoji',
        'fullsize',
      ],
      emoji: { enableAutoComplete: true, recentCountLimit: 50 },
      uploader: { insertImageAsBase64URI: true },
      style: { border: 0, boxShadow: 'none', background: 'none' }, // borderless in edit
    }),
    []
  );

  // Fetch post on open or postId change (edit mode)
  useEffect(() => {
    async function fetchPost() {
      if (!postId) {
        reset({ title: '', body: '' });
        setEditorContent('');
        setPostUrl('');
        setPreviewMode(false); // reset preview mode on new
        return;
      }
      setLoading(true);
      try {
        const { data } = await axios.get(`${CRUD.appContent.appPosts}/${postId}`);
        console.log(data.body)
        reset({ title: data.title || '', body: data.body || '' });
        setEditorContent(data.body || '');
        setPostUrl(data.url || '');
        setPreviewMode(false); // reset preview mode on fetch
      } catch (error) {
        enqueueSnackbar(`Failed to load post data: ${getErrorMessage(error)}`, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
    if (open) {
      fetchPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, reset, enqueueSnackbar, open]);

  // Jodit editor content change syncing with react-hook-form
  const handleEditorChange = content => {
    setEditorContent(content);
    setValue('body', content, { shouldValidate: true, shouldDirty: true });
  };

  // Submit handler
  const onSubmit = async data => {
    setLoading(true);
    try {
      const payload = { title: data.title, body: data.body };
      if (postId) {
        const { data: updated } = await axios.put(`${CRUD.appContent.appPosts}/${postId}`, payload);
        enqueueSnackbar('Post updated successfully!', { variant: 'success' });
        // update editor content with latest from server (assuming the response contains updated content)
        setEditorContent(updated.body || data.body);
        setPreviewMode(true); // enable preview after update
      } else {
        await axios.post(CRUD.appContent.appPosts, payload);
        enqueueSnackbar('Post created successfully!', { variant: 'success' });
        // reset editor for new post after creation
        reset();
        // setEditorContent('');
        setPreviewMode(false);
        // setPostUrl('');
      }
      handleComplete();
    } catch (e) {
      enqueueSnackbar(`Failed to save post: ${getErrorMessage(e)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Copy URL function
  const handleCopyUrl = () => {
    if (postUrl) {
      navigator.clipboard.writeText(postUrl);
      enqueueSnackbar('URL copied to clipboard', { variant: 'success' });
    }
  };

  // Toggle preview mode (enable for both create and edit)
  const togglePreview = () => setPreviewMode(prev => !prev);

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={() => {}}
      PaperProps={{ sx: { bgcolor: theme.palette.background.default, position: 'relative' } }}
      aria-labelledby="manage-post-dialog-title"
      disableEscapeKeyDown
    >
      {/* NavBar */}
      <AppBar
        position="static"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 1, sm: 3 },
            py: { xs: 1, sm: 2 },
            minHeight: 64,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" lineHeight={1.1}>
              Manage App Post
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {postId ? 'Edit Blog Post' : 'Create Blog Post'}
            </Typography>
          </Box>

          <Tooltip title="Close">
            <IconButton
              color="inherit"
              onClick={() => {
                reset();
                setPreviewMode(false);
                handleClose();
              }}
              disabled={loading || isSubmitting}
              aria-label="close dialog"
              size="large"
            >
              <Icon icon="mdi:close" fontSize={28} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Divider />

      {/* Main Form Content */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          overflowY: 'auto',
          pb: 3,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 740,
            mx: 'auto',
            mt: { xs: 3, sm: 7 },
            bgcolor: theme.palette.background.paper,
            borderRadius: 4,
            p: { xs: 2, sm: 4 },
            mb: 6,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {/* Preloader overlay */}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.75)',
                zIndex: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 4,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <FormProvider {...methods}>
            <form
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}
            >
              {/* Title Field */}
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    placeholder="Enter the post title"
                    variant="outlined"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={loading}
                    size="medium"
                  />
                )}
              />

              {/* URL (edit only) */}
              {postId && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Post URL"
                    value={postUrl}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    size="small"
                    disabled
                    sx={{
                      bgcolor: theme.palette.action.disabledBackground,
                      borderRadius: 1,
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: theme.palette.text.primary,
                        opacity: 1,
                      },
                    }}
                  />
                  <Tooltip title="Copy URL to clipboard">
                    <IconButton
                      onClick={handleCopyUrl}
                      size="medium"
                      aria-label="copy url"
                      sx={{
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': { bgcolor: theme.palette.action.hover },
                      }}
                    >
                      <Icon icon="mdi:clipboard-outline" fontSize={23} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}

              {/* Body Label + Editor/Preview */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 500, mb: 1, pl: 0.5, color: theme.palette.text.secondary }}
                  id="body-label"
                  gutterBottom
                >
                  Body
                </Typography>

                {previewMode ? (
                  <Box
                    sx={{
                      minHeight: 210,
                      border: `1.5px solid ${theme.palette.divider}`,
                      background: theme.palette.action.hover,
                      color: theme.palette.text.primary,
                      p: 2,
                      borderRadius: 2,
                      fontSize: '1rem',
                    }}
                    dangerouslySetInnerHTML={{ __html: editorContent || '<i>No content</i>' }}
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 210,
                      background: theme.palette.background.default,
                      p: 0,
                      borderRadius: 2,
                      border: 'none !important', // no border in edit mode
                      '& > div': {
                        border: 0,
                        boxShadow: 'none !important',
                        borderRadius: 2,
                      },
                    }}
                  >
                    <JoditEditor
                      ref={editor}
                      value={editorContent}
                      config={editorConfig}
                      tabIndex={1}
                      onChange={handleEditorChange}
                      disabled={loading}
                      aria-labelledby="body-label"
                    />
                  </Box>
                )}

                {errors.body && (
                  <Typography variant="caption" color="error" sx={{ pl: 1, mt: 0.5 }}>
                    {errors.body.message}
                  </Typography>
                )}
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center" mt={3}>
                {/* preview toggle available for both create and edit */}
                <Tooltip title={previewMode ? 'Edit Post' : 'Preview'}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={togglePreview}
                    disabled={loading || isSubmitting}
                    startIcon={<Icon icon="mdi:eye-outline" />}
                    size="large"
                  >
                    {previewMode ? 'Edit' : 'Preview'}
                  </Button>
                </Tooltip>

                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={loading || isSubmitting || previewMode}
                  startIcon={<Icon icon="mdi:content-save-outline" />}
                  size="large"
                  sx={{ fontWeight: 700, minWidth: 100 }}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </Dialog>
  );
}
